/**
 * Türkçe-aware arama yardımcısı.
 *
 * İki sorunu birden çözer:
 * 1) Büyük/küçük harf — İ/I/i/ı locale'e duyarlı (toLocaleLowerCase('tr'))
 * 2) Diakritik (Türkçe karakter) — ş↔s, ç↔c, ü↔u, ö↔o, ğ↔g, ı↔i
 *    Yani "ozel" yazınca "Özel"i, "sefa" yazınca "Şefa"yı bulur.
 *
 * Sistem genelindeki tüm arama filtrelerinde bu helper kullanılmalı —
 * `.toLowerCase().includes(q.toLowerCase())` pattern'i artık yasaktır.
 */

const TR_DIACRITIC_MAP = {
  ç: 'c', ş: 's', ğ: 'g', ı: 'i', ö: 'o', ü: 'u',
  â: 'a', î: 'i', û: 'u', é: 'e', è: 'e', ê: 'e',
};

/**
 * Bir string'i arama için normalize eder:
 * - Türkçe locale lowercase (İ → i, I → ı → i)
 * - Diakritik karakterleri ASCII karşılığına çevirir
 * - Trim
 *
 * Hızlı yol: küçük string'ler için zincirleme replace yerine tek pass.
 */
export const normalizeForSearch = (value) => {
  if (value == null) return '';
  // Lowercase önce — sonra map sadece küçük karakterler için yeter
  const lowered = String(value).toLocaleLowerCase('tr-TR');
  let out = '';
  for (let i = 0; i < lowered.length; i++) {
    const ch = lowered[i];
    out += TR_DIACRITIC_MAP[ch] ?? ch;
  }
  return out.trim();
};

/**
 * Hem haystack hem needle'ı normalize ederek includes kontrolü yapar.
 * needle boş/null ise true döner (filtrelenmemiş davranış).
 *
 * Örnek:
 *   searchMatch('Şefa Lokantası', 'sefa') === true
 *   searchMatch('İstanbul Tedarikçisi', 'istanbul') === true
 *   searchMatch('Özgür Gıda', 'OZGUR') === true
 */
export const searchMatch = (haystack, needle) => {
  if (needle == null || needle === '') return true;
  return normalizeForSearch(haystack).includes(normalizeForSearch(needle));
};

/**
 * needle'ın ZATEN normalize edildiği varsayılır (performans için).
 * Tipik kullanım: filter callback'lerinde her satır için tek tek
 * normalize etmek yerine, needle'ı bir kere `normalizeForSearch` ile
 * hazırlayıp her satırda bunu çağırmak.
 *
 *   const q = normalizeForSearch(search);
 *   list.filter(item => searchMatchPre(item.ad, q));
 */
export const searchMatchPre = (haystack, normalizedNeedle) => {
  if (normalizedNeedle == null || normalizedNeedle === '') return true;
  return normalizeForSearch(haystack).includes(normalizedNeedle);
};

/**
 * Çok alanlı arama — bir kayıttaki birden fazla alandan herhangi biri
 * eşleşirse true döner.
 *
 * Örnek:
 *   searchMatchAny(t, q, ['ad', 'unvan', 'vergi_no'])
 */
export const searchMatchAny = (record, needle, fields) => {
  if (needle == null || needle === '') return true;
  if (!record) return false;
  const n = normalizeForSearch(needle);
  for (const field of fields) {
    if (normalizeForSearch(record[field]).includes(n)) return true;
  }
  return false;
};
